/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface SpecialtyTeamProps {
  id: string;
  name: string;
  affiliationId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class SpecialtyTeamRecord extends Record<SpecialtyTeamProps>({
  id: null as any,
  name: null as any,
  affiliationId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    affiliationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): SpecialtyTeamRecord {
    if (json == null) {
      throw new Error('SpecialtyTeamRecord.fromJS: parameter cannot be null');
    }
    return new SpecialtyTeamRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'SpecialtyTeamJson.name'),
      affiliationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'affiliationId', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamJson.affiliationId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    affiliationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [SpecialtyTeamRecord, Error[]] {
    if (json == null) {
      throw new Error('SpecialtyTeamRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new SpecialtyTeamRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'SpecialtyTeamJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      affiliationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'affiliationId', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamJson.affiliationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: SpecialtyTeamProps) {
    super(props);
    if (props == null) {
      throw new Error('SpecialtyTeamRecord constructor parameter cannot be null');
    }
  }
}

export abstract class SpecialtyTeamViewModelBase implements SpecialtyTeamProps {
  protected readonly _props: SpecialtyTeamProps;
  private _json?: SpecialtyTeamProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get affiliationId(): string {
    return this._props.affiliationId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: SpecialtyTeamProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        affiliationId: this._props.affiliationId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllSpecialtyTeamEntities = (state: AppState) =>
  state.entityStores.specialtyTeam.entities;

export const selectSpecialtyTeamById = (id: string) => (state: AppState) =>
  state.entityStores.specialtyTeam.entities.get(id);

export const selectSpecialtyTeamMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.specialtyTeam.meta.get(id);

export const selectSpecialtyTeamAndMetadataById = (id: string) =>
  createSelector(
    selectSpecialtyTeamById(id),
    selectSpecialtyTeamMetadataById(id),
    (specialtyTeam, meta) => ({ specialtyTeam, meta }),
  );

export function selectSpecialtyTeamEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllSpecialtyTeamEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, SpecialtyTeamRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectSpecialtyTeamEntitiesByName(name: string) {
  return createSelector(selectAllSpecialtyTeamEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectSpecialtyTeamEntitiesByAffiliationId(affiliationId: string) {
  return createSelector(selectAllSpecialtyTeamEntities, (entities) =>
    entities.filter((e) => e.affiliationId === affiliationId).valueSeq(),
  );
}

export function selectSpecialtyTeamEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllSpecialtyTeamEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
